import type { Editor } from '@tiptap/react';
import React from 'react';

import { EditorFeatures, EditorModalStates } from '../types';
import { LinkEditPopup } from './LinkEditPopup';
import { LinkHoverTooltip } from './LinkHoverTooltip';
import {
  EditorConvertUrlModal,
  EditorIframeCodeModal,
  EditorInsertTableModal,
  EditorLinkAddModal,
} from './modals';

export interface EditorModalsProps {
  editor: Editor | null;
  features: EditorFeatures;
  modalStates: EditorModalStates;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventHandlers: Record<string, any>;
  enableLinkHoverTooltip?: boolean;
  enableLinkClickModal?: boolean;
}

/**
 * EditorModals
 * Modal components for the editor (link modal, image modal, etc.)
 */
export const EditorModals: React.FC<EditorModalsProps> = ({
  editor,
  features,
  modalStates,
  eventHandlers,
  enableLinkHoverTooltip = false,
  enableLinkClickModal = false,
}) => {
  if (!editor) {
    return null;
  }

  if (!modalStates) {
    return null;
  }

  return (
    <>
      {/* Link Add Modal */}
      <EditorLinkAddModal
        isOpen={features.links && modalStates?.isLinkAddModalOpen}
        value={modalStates?.linkAddModalParams}
        initialValue={modalStates?.linkAddModalInitialParams}
        onChange={(params) => eventHandlers?.setLinkAddModalParams?.(params)}
        onSubmit={() =>
          eventHandlers?.handleSubmitLink?.({
            url: modalStates?.linkAddModalParams?.url || '',
            text: modalStates?.linkAddModalParams?.text || '',
          })
        }
        onClose={() => eventHandlers?.handleCloseLinkAddModal?.()}
        errors={modalStates?.linkUrlErrors || {}}
      />

      {/* Convert URL Modal */}
      <EditorConvertUrlModal
        isOpen={modalStates?.isConvertUrlModalOpen || false}
        value={modalStates?.convertUrl || ''}
        onChange={(url) => eventHandlers?.setConvertUrl?.(url)}
        onSubmit={() => eventHandlers?.handleSubmitConvertUrl?.(modalStates?.convertUrl || '')}
        onClose={() => eventHandlers?.closeConvertUrlModal?.()}
        errors={modalStates?.convertUrlErrors || {}}
      />

      {/* IFrame Code Modal */}
      <EditorIframeCodeModal
        isOpen={features.iframes && (modalStates?.isIFrameCodeModalOpen || false)}
        value={modalStates?.iFrameCode || ''}
        onChange={(code) => eventHandlers?.setIFrameCode?.(code)}
        onSubmit={() => eventHandlers?.handleSubmitIFrameCode?.(modalStates?.iFrameCode || '')}
        onClose={() => eventHandlers?.closeIFrameCodeModal?.()}
        errors={modalStates?.iFrameCodeErrors || {}}
      />

      {/* Insert Table Modal */}
      <EditorInsertTableModal
        isOpen={features.tables && (modalStates?.isInsertTableModalOpen || false)}
        editor={editor}
        tableRows={modalStates?.tableRows || '3'}
        tableCols={modalStates?.tableCols || '3'}
        onTableRowsChange={(rows) => eventHandlers?.setTableRows?.(rows)}
        onTableColsChange={(cols) => eventHandlers?.setTableCols?.(cols)}
        onSubmit={() =>
          eventHandlers?.handleInsertTable?.({
            rows: parseInt(modalStates?.tableRows || '3'),
            cols: parseInt(modalStates?.tableCols || '3'),
          })
        }
        onClose={() => eventHandlers?.closeInsertTableModal?.()}
        errors={modalStates?.tableErrors || {}}
      />

      {/* Link Hover Tooltip */}
      {enableLinkHoverTooltip && modalStates?.isLinkHoverTooltipOpen && (
        <div
          className='absolute z-[9999]'
          style={{
            top: modalStates?.linkHoverTooltipPosition?.top || 0,
            left: modalStates?.linkHoverTooltipPosition?.left || 0,
          }}
        >
          <LinkHoverTooltip url={modalStates?.linkHoverTooltipUrl || ''} />
        </div>
      )}

      {/* Link Edit Popup */}
      {enableLinkClickModal && (
        <LinkEditPopup
          isOpen={modalStates?.isLinkEditPopupOpen || false}
          onClose={() => eventHandlers?.setIsLinkEditPopupOpen?.(false)}
          onDelete={() => {
            eventHandlers?.handleDeleteLink?.(() => {
              eventHandlers?.setIsLinkEditPopupOpen?.(false);
            });
          }}
          onEdit={() => {
            eventHandlers?.setIsLinkEditPopupOpen?.(false);
            eventHandlers?.handleOpenLinkAddModal?.();
          }}
          value={modalStates?.linkAddModalParams || { url: '', text: '' }}
        />
      )}
    </>
  );
};
